import { get } from 'lodash-es';
import { getSyncDate } from 'site-modules/shared/components/profile/idm/idm-utils';
import { getCalculatedPrices } from 'site-modules/shared/utils/car-buying/calculated-prices';
import { LEASE_FINANCE_METHOD, LOAN_FINANCE_METHOD } from 'site-modules/shared/constants/car-buying';
import { NON_CAPITALIZED_TAXES_AND_FEES_SUM } from 'site-modules/shared/constants/calculator/calculator';
import { getTradeInDetails } from 'site-modules/shared/utils/car-buying/calculator-util';

/**
 * Formats taxes and fees to insider acceptable data format.
 * @param {object} taxes
 * @param {object} fees
 * @returns {{fees: object[], taxes: object[]}}
 */
function formatTaxesAndFees(taxes, fees) {
  return {
    taxes: taxes
      ? Object.keys(taxes).map(key => ({
          name: key,
          amount: taxes[key],
          capitalized: true,
        }))
      : [],
    fees: fees
      ? Object.keys(fees).map(key => ({
          name: key,
          amount: fees[key],
          capitalized: true,
        }))
      : [],
  };
}

/**
 * Maps incentives to insider acceptable data format.
 * @param {object[]} incentives
 * @returns {object[]|array}
 */
function mapIncentives(incentives) {
  return incentives
    ? incentives.map(({ programName, cash, expirationDate, expireDate }) => ({
        name: programName,
        value: cash,
        expiryTs: new Date(expirationDate || expireDate).toISOString(),
      }))
    : [];
}

/**
 * Gets financing terms for loan and lease.
 * @param {boolean} isPurchase
 * @param {number} numberOfMonths
 * @param {number} apr
 * @param {number} downPayment
 * @param {number} annualMileage
 * @returns {object}
 */
function getFinancingTerms({ isPurchase, numberOfMonths, apr, downPayment, annualMileage }) {
  return isPurchase
    ? { loanPreferences: { numberOfMonths, financeRate: apr, downPayment } }
    : { leasePreferences: { numberOfMonths, annualMileage, downPayment } };
}

/**
 * Format deal attributes for single deal selection.
 * @param {object} vehicle
 * @param {object} dealDetails
 * @param {boolean} isPurchase
 * @returns {object}
 */
export function formatDealAttributes(vehicle, dealDetails, isPurchase) {
  const {
    dueAtSigning,
    monthlyPayment,
    paymentWithoutTaxes,
    paymentWithTaxes,
    term: numberOfMonths,
    annualMileage,
    downPayment,
    sellingPrice,
    totalRebate,
    moneyFactor,
    apr,
    residual: residualPercentage,
    incentives,
    storedZipCode,
    preQualified,
    leadId,
    endDateEpochMillis,
    tradeIn,
    storedSelections,
    saveTradeInDetails,
  } = dealDetails;
  const { baseMonthlyPayment } = paymentWithoutTaxes;
  const {
    amountFinanced,
    capitalizedFees,
    securityDeposit = 0,
    capitalizedTaxes,
    nonCapitalizedTaxesAndFees,
  } = paymentWithTaxes;
  const { displayPrice } = vehicle.prices;

  const taxesAndFees = { ...formatTaxesAndFees(capitalizedTaxes, capitalizedFees) };
  taxesAndFees.fees.push({ name: NON_CAPITALIZED_TAXES_AND_FEES_SUM, amount: nonCapitalizedTaxesAndFees });

  const { purchasePrice, marketAdjustment } = getCalculatedPrices({ sellingPrice, totalRebate, msrp: displayPrice });

  let tradeInDetails;

  if (saveTradeInDetails) {
    tradeInDetails = getTradeInDetails({
      selections: storedSelections,
      appliedTradeIn: tradeIn,
      limitCriteria: get(dealDetails, 'limitCriteria', {}),
      pubState: vehicle.type,
    });
  }

  return {
    financing: getFinancingTerms({ isPurchase, numberOfMonths, apr, downPayment, annualMileage }),
    edmundsDeals: [
      {
        vin: vehicle.vin,
        financeMethod: isPurchase ? LOAN_FINANCE_METHOD : LEASE_FINANCE_METHOD,
        zipCode: storedZipCode,
        monthlyPayment,
        baseMonthlyPayment,
        dueAtSigning,
        downPayment,
        numberOfMonths,
        mileage: annualMileage,
        capCost: sellingPrice,
        msrp: displayPrice,
        estimatedPurchasePrice: purchasePrice,
        amountFinanced,
        marketAdjustment,
        moneyFactor,
        apr,
        residualValue: isPurchase ? undefined : displayPrice * residualPercentage,
        residualPercentage,
        totalRebate,
        rebates: mapIncentives(incentives),
        expiryTs: isPurchase ? undefined : new Date(endDateEpochMillis).toISOString(),
        createdTs: getSyncDate(),
        preQualified,
        leadReference: leadId && `#/leads/${leadId}`,
        securityDeposit,
        ...taxesAndFees,
        tradeInValue: tradeIn,
        tradeIn: tradeInDetails,
      },
    ],
  };
}
